import React, { useCallback, useEffect, useState } from "react";
import "./LoginPage.css";
import { useLocation, useNavigate } from "react-router-dom";
import {
  apiAuthenticate,
  apiGetAuthTypes,
  apiSendAuthCode,
} from "../../../utils/queries";
import { trackPromise } from "react-promise-tracker";
import { FaShieldAlt, FaKey } from "react-icons/fa";

const formatTimer = (ms) => {
  const total = Math.ceil(ms / 1000);
  const m = Math.floor(total / 60).toString().padStart(2, "0");
  const s = (total % 60).toString().padStart(2, "0");
  return `${m}:${s}`;
};

const maskValue = (channel, value) => {
  if (!value) return value;
  if (channel === "SMS") return value;
  // mask email: j***@company.com
  const at = value.indexOf("@");
  if (at <= 1) return value;
  return value[0] + "***" + value.slice(at);
};


export const LoginPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [token, setToken] = useState(null);
  const [authType, setAuthType] = useState(null);
  const [selectedAuthType, setSelectedAuthType] = useState(null);
  const [authTypes, setAuthTypes] = useState(null);
  const [code, setCode] = useState("");
  const [codeErrorMessage, setCdeErrorMessage] = useState("Required");
  const [codeError, setCodeError] = useState(false);
  const [timeLeft, setTimeLeft] = useState(0);

  useEffect(() => {
    let intervalId;
    if (timeLeft > 0) {
      intervalId = setInterval(() => {
        setTimeLeft((t) => t - 1000);
      }, 1000);
    }
    return () => clearInterval(intervalId);
  }, [timeLeft]);

  const start = () => setTimeLeft(60 * 5 * 1000);

  const restart = useCallback(() => {
    apiSendAuthCode({ channel: authType.channel.toLowerCase() }, token).then();
    start();
  }, [token, authType]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setCodeError(false);
    if (!code) {
      setCodeError(true);
      setCdeErrorMessage("Required");
      return;
    }
    try {
      const result = await apiAuthenticate(token, code, authType.channel.toLowerCase());
      localStorage.setItem("authToken", result.data);
      navigate(`/${token}/`);
    } catch {
      setCodeError(true);
      setCdeErrorMessage("Invalid code");
    }
  };

  useEffect(() => {
    const state = location.state;
    if (state?.token == null) {
      navigate(`/tokenfail`);
    } else {
      setToken(state.token);
    }
  }, [location]);

  useEffect(() => {
    if (token === null) return;
    async function getAuthTypes() {
      try {
        const response = await trackPromise(apiGetAuthTypes(token));
        if (response.data.length === 0) {
          navigate(`/tokenfail`, { state: { message: "No authentication method." } });
        } else if (response.data.length === 1) {
          await trackPromise(apiSendAuthCode({ channel: response.data[0].channel.toLowerCase() }, token));
          setAuthType(response.data[0]);
          start();
        } else {
          setAuthTypes(response.data);
        }
      } catch {
        navigate(`/tokenfail`);
      }
    }
    getAuthTypes();
  }, [token]);

  // ── Method picker ──────────────────────────────────────────────────────────
  if (authTypes && authType === null) {
    return (
      <div className="emp-login-page">
        <div className="emp-login-card">
          <div className="emp-login-card__hero">
            <div className="emp-login-card__icon-wrapper">
              <FaShieldAlt />
            </div>
            <h1 className="emp-login-card__title">Verify it's you</h1>
            <p className="emp-login-card__subtitle">
              Choose where you'd like to receive your one-time code.
            </p>
          </div>

          <div className="emp-login-card__body">
            <p className="emp-login-card__lead">
              We'll send a 6-digit code to confirm this is you. The code is valid for a few minutes.
            </p>

            <div className="emp-login-card__field">
              <label className="emp-login-card__label" htmlFor="login-channel">
                Send code to
              </label>
              <div className="emp-login-card__select-wrap">
                <select
                  id="login-channel"
                  className="emp-login-card__select"
                  defaultValue=""
                  onChange={(e) => {
                    if (!e.target.value) { setSelectedAuthType(null); return; }
                    setSelectedAuthType(JSON.parse(e.target.value));
                  }}
                >
                  <option value="">Select…</option>
                  {authTypes.map((type) => (
                    <option key={type.channel} value={JSON.stringify(type)}>
                      {type.channel === "SMS" ? "Mobile: " : "E-mail: "} {maskValue(type.channel, type.value)}
                    </option>
                  ))}
                </select>
                <span className="emp-login-card__select-caret">▾</span>
              </div>
            </div>

            <div className="emp-login-card__footer">
              <span />
              <button
                className="emp-login-card__btn"
                disabled={selectedAuthType === null}
                onClick={async () => {
                  if (!selectedAuthType) return;
                  await trackPromise(apiSendAuthCode({ channel: selectedAuthType.channel.toLowerCase() }, token));
                  setAuthType(selectedAuthType);
                  start();
                }}
              >
                Send code
              </button>
            </div>

            <hr className="emp-login-card__divider" />
            <p className="emp-login-card__support">
              Trouble signing in? Contact{" "}
              <a href="mailto:vendorsupport@hafniabw.com">vendorsupport@hafniabw.com</a>.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // ── Loading ────────────────────────────────────────────────────────────────
  if (!authType) {
    return (
      <div className="emp-login-page">
        <div className="emp-login-card">
          <div className="emp-login-card__hero">
            <div className="emp-login-card__icon-wrapper"><FaShieldAlt /></div>
            <h1 className="emp-login-card__title">Verify it's you</h1>
            <p className="emp-login-card__subtitle">Loading…</p>
          </div>
        </div>
      </div>
    );
  }

  // ── Code entry ─────────────────────────────────────────────────────────────
  const isEmail = authType.channel !== "SMS";
  const masked = maskValue(authType.channel, authType.value);

  return (
    <div className="emp-login-page">
      <div className="emp-login-card">
        <div className="emp-login-card__hero">
          <div className="emp-login-card__icon-wrapper">
            <FaKey />
          </div>
          <h1 className="emp-login-card__title">Enter your code</h1>
          <p className="emp-login-card__subtitle">
            We just sent a one-time code to your {isEmail ? "email address" : "phone"}.
          </p>
        </div>

        <div className="emp-login-card__body">
          <p className="emp-login-card__lead">
            Sent to{" "}
            <span className="emp-login-channel-pill">
              {isEmail ? "✉" : "📱"} {masked}
            </span>
            . Enter the code below to continue.
          </p>

          <form onSubmit={handleLogin}>
            <div className="emp-login-card__field">
              <label className="emp-login-card__label" htmlFor="login-code">
                One-time code
              </label>
              <input
                id="login-code"
                type="text"
                className={`emp-login-code-input${codeError ? " is-invalid" : ""}`}
                placeholder="••••••"
                autoComplete="one-time-code"
                value={code}
                onChange={(e) => { setCode(e.target.value); setCodeError(false); }}
              />
              {codeError && (
                <p className="emp-login-card__error">{codeErrorMessage}</p>
              )}
            </div>

            <div className="emp-login-card__footer">
              <span className="emp-login-card__resend">
                {timeLeft > 0 ? (
                  <>
                    Didn't get the code? Resend in{" "}
                    <span className="emp-login-card__resend-timer">{formatTimer(timeLeft)}</span>
                  </>
                ) : (
                  <>
                    Didn't get the code?{" "}
                    <button type="button" className="emp-login-card__link" onClick={restart}>
                      Resend code
                    </button>
                  </>
                )}
              </span>
              <button type="submit" className="emp-login-card__btn">
                Continue
              </button>
            </div>
          </form>

          <hr className="emp-login-card__divider" />
          <p className="emp-login-card__support">
            {authTypes !== null && (
              <>
                <button
                  type="button"
                  className="emp-login-card__link"
                  onClick={() => { setAuthType(null); setCode(""); setCodeError(false); }}
                >
                  Use a different method
                </button>
                <br />
              </>
            )}
            Trouble signing in? Contact{" "}
            <a href="mailto:vendorsupport@hafniabw.com">vendorsupport@hafniabw.com</a>.
          </p>
        </div>
      </div>
    </div>
  );
};
